import { Container, Graphics, Sprite, Text, TextStyle, Rectangle } from 'pixi.js';
import type { Texture } from 'pixi.js';
import { PIECE_VALUES } from '@blind-kings/game-core';
import type { SpriteAnimState } from '../../../characters';
import { advanceFrame, isLoopingState, stripDurationMs } from '../../../characters';
import type { TeamPalette } from '../../../player-colors';
import {
  loadPieceVisual,
  paletteForTeam,
  spritePackForTeam,
  type PieceTint,
} from './assets';

export type PieceNodeOptions = {
  id: number;
  value: number;
  teamIndex?: number;
  cellSize: number;
  displayValue?: boolean;
  interactive?: boolean;
  teamPalettes?: Partial<Record<0 | 1, TeamPalette>>;
};

export class PieceNode extends Container {
  pieceId: number;
  pieceValue: number;
  teamIndex?: number;
  private teamPalettes?: Partial<Record<0 | 1, TeamPalette>>;
  private sprite = new Sprite();
  private valueBadge: Text | null = null;
  private outline = new Graphics();
  private baseScale = 1;
  private hovered = false;
  private selected = false;
  private pulsing = false;
  private pulsePhase = 0;
  private lastIdentityKey = '';
  private wantsDisplayValue = false;
  private lastCellSize = 0;
  private frames: Texture[] = [];
  private fps = 0;
  private frameIndex = 0;
  private frameAccum = 0;
  private animState: SpriteAnimState = 'resting';
  private loopAnim = true;
  private visualGen = 0;

  constructor(opts: PieceNodeOptions) {
    super();
    this.pieceId = opts.id;
    this.pieceValue = opts.value;
    this.teamIndex = opts.teamIndex;
    this.teamPalettes = opts.teamPalettes;
    this.wantsDisplayValue = opts.displayValue === true;
    this.lastCellSize = opts.cellSize;
    this.eventMode = opts.interactive === false ? 'none' : 'static';
    this.cursor = opts.interactive === false ? 'default' : 'pointer';

    this.sprite.anchor.set(0.5);
    this.sprite.tint = 0xffffff;
    this.addChild(this.sprite);
    this.addChild(this.outline);

    this.syncValueBadge(opts.cellSize);
    void this.applyVisual(opts.cellSize);
  }

  setTeamPalettes(teamPalettes?: Partial<Record<0 | 1, TeamPalette>>) {
    this.teamPalettes = teamPalettes;
  }

  setDisplayValue(show: boolean, cellSize = this.lastCellSize) {
    this.wantsDisplayValue = show;
    this.syncValueBadge(cellSize);
  }

  private shouldShowValueBadge() {
    return (
      this.wantsDisplayValue &&
      this.pieceValue !== PIECE_VALUES.Unturned &&
      this.pieceValue !== PIECE_VALUES.Empty
    );
  }

  private syncValueBadge(cellSize: number) {
    this.lastCellSize = cellSize;
    if (this.shouldShowValueBadge()) {
      if (!this.valueBadge) {
        this.valueBadge = new Text({
          text: String(this.pieceValue),
          style: new TextStyle({
            fontFamily: 'ui-sans-serif, system-ui, sans-serif',
            fontSize: 14,
            fontWeight: '700',
            fill: 0xf0ece4,
            stroke: { color: 0x0a0e17, width: 3 },
          }),
        });
        this.valueBadge.anchor.set(1, 1);
        this.addChild(this.valueBadge);
      } else {
        this.valueBadge.text = String(this.pieceValue);
        this.valueBadge.visible = true;
      }
      this.valueBadge.position.set(cellSize * 0.34, cellSize * 0.34);
      this.valueBadge.style.fontSize = Math.max(10, Math.floor(cellSize * 0.18));
    } else if (this.valueBadge) {
      this.disposeValueBadge();
    }
  }

  private currentTint(): PieceTint {
    return paletteForTeam(this.teamIndex, this.teamPalettes);
  }

  private currentPackId(): string {
    return spritePackForTeam(this.teamIndex, this.teamPalettes);
  }

  private identityKey(): string {
    const tint = this.currentTint();
    return `${this.currentPackId()}:${this.pieceValue}:${this.animState}:${tint.fill}:${tint.stroke}`;
  }

  async setAnimState(
    state: SpriteAnimState,
    opts?: { loop?: boolean; cellSize?: number }
  ) {
    const loop = opts?.loop ?? isLoopingState(state);
    if (this.animState === state && this.loopAnim === loop) return;
    this.animState = state;
    this.loopAnim = loop;
    await this.applyVisual(opts?.cellSize ?? this.lastCellSize);
  }

  /** Return to the idle clip the current pointer state calls for. */
  async returnToAmbient(cellSize = this.lastCellSize) {
    await this.setAnimState(this.hovered ? 'hover' : 'resting', { cellSize });
  }

  /** Play a one-shot clip and resolve when it finishes (or immediately if static). */
  async playAnimOnce(state: SpriteAnimState, cellSize = this.lastCellSize) {
    await this.setAnimState(state, { loop: false, cellSize });
    const durationMs = stripDurationMs({
      url: '',
      frameCount: this.frames.length,
      fps: this.fps,
    });
    if (this.frames.length <= 1 || durationMs <= 0) return;
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, durationMs);
    });
  }

  private isLive() {
    return !this.destroyed && !!this.sprite && !this.sprite.destroyed && !!this.sprite.scale;
  }

  async applyVisual(cellSize: number) {
    const gen = ++this.visualGen;
    const tint = this.currentTint();
    const packId = this.currentPackId();
    const visual = await loadPieceVisual(
      this.pieceValue,
      tint,
      packId,
      this.animState
    );
    if (!this.isLive() || gen !== this.visualGen) return;
    this.lastIdentityKey = this.identityKey();
    this.frames = visual.frames;
    this.fps = visual.fps;
    this.frameIndex = 0;
    this.frameAccum = 0;
    this.setTexture(this.frames[0] ?? this.sprite.texture, cellSize);
  }

  setTexture(texture: Texture, cellSize: number) {
    if (!this.isLive()) return;
    this.sprite.texture = texture;
    this.sprite.tint = 0xffffff;
    const max = cellSize * 0.78;
    const scale = Math.min(
      max / Math.max(texture.width, 1),
      max / Math.max(texture.height, 1)
    );
    this.baseScale = scale;
    this.sprite.scale.set(scale);

    const pad = cellSize * 0.08;
    // Keep a usable touch target even on tiny phones.
    const visualHit = Math.max(texture.width, texture.height) * scale + pad * 2;
    const minHit = Math.max(cellSize * 0.85, 36);
    const hit = Math.max(visualHit, minHit);
    this.hitArea = new Rectangle(-hit / 2, -hit / 2, hit, hit);

    this.syncValueBadge(cellSize);
    this.redrawOutline(cellSize);
    this.refreshHoverScale();
  }

  /** Rescale in place without reloading frames (used on board resize). */
  layoutToCellSize(cellSize: number) {
    if (!this.isLive() || cellSize <= 0) return;
    if (Math.abs(cellSize - this.lastCellSize) < 0.05) return;
    const texture = this.sprite.texture;
    if (texture && texture.width > 0 && texture.height > 0) {
      this.setTexture(texture, cellSize);
      return;
    }
    this.lastCellSize = cellSize;
    this.syncValueBadge(cellSize);
    this.redrawOutline(cellSize);
  }

  async setPieceIdentity(
    value: number,
    teamIndex: number | undefined,
    cellSize: number,
    teamPalettes?: Partial<Record<0 | 1, TeamPalette>>
  ) {
    this.pieceValue = value;
    this.teamIndex = teamIndex;
    if (teamPalettes) this.teamPalettes = teamPalettes;
    this.syncValueBadge(cellSize);
    await this.applyVisual(cellSize);
  }

  setSelected(selected: boolean, cellSize: number) {
    this.selected = selected;
    this.redrawOutline(cellSize);
    if (selected) {
      this.hovered = true;
    }
    this.refreshHoverScale();
    this.syncAmbientState();
  }

  setHovered(hovered: boolean) {
    // Keep the emphasis while selected even after the pointer leaves (touch).
    const next = hovered || this.selected;
    if (this.hovered === next) return;
    this.hovered = next;
    this.refreshHoverScale();
    this.syncAmbientState();
  }

  /** Hover swaps the idle clip; an action clip owns the piece until it ends. */
  private syncAmbientState() {
    if (this.animState !== 'resting' && this.animState !== 'hover') return;
    void this.setAnimState(this.hovered ? 'hover' : 'resting');
  }

  setPulsing(pulsing: boolean) {
    this.pulsing = pulsing;
    if (!this.isLive()) return;
    if (!pulsing) {
      this.sprite.alpha = 1;
      this.pulsePhase = 0;
      this.refreshHoverScale();
    }
  }

  /** Brief press feedback for touch/mouse down (independent of hover). */
  flashPress() {
    if (!this.isLive()) return;
    this.sprite.scale.set(this.baseScale * 1.18);
    window.setTimeout(() => {
      if (this.isLive()) this.refreshHoverScale();
    }, 120);
  }

  tickPulse(dt: number) {
    if (!this.isLive() || (!this.pulsing && !this.selected)) return;
    // Selected bounce is intentionally slower than flip-pulse so it feels calm.
    this.pulsePhase += dt * (this.selected && !this.pulsing ? 0.0035 : 0.01);
    if (this.pulsing) {
      this.sprite.alpha = 0.55 + Math.sin(this.pulsePhase) * 0.35;
    }
    if (this.selected) {
      const wobble = 1.06 + Math.sin(this.pulsePhase) * 0.035;
      this.sprite.scale.set(this.baseScale * wobble);
    }
  }

  tickAnim(dt: number) {
    if (!this.isLive()) return;
    const next = advanceFrame({
      frameIndex: this.frameIndex,
      accumulatedMs: this.frameAccum,
      elapsedMs: dt,
      frameCount: this.frames.length,
      fps: this.fps,
      loop: this.loopAnim,
    });
    this.frameIndex = next.frameIndex;
    this.frameAccum = next.accumulatedMs;
    if (next.changed) {
      this.sprite.texture = this.frames[this.frameIndex];
    }
  }

  needsRetint(teamPalettes?: Partial<Record<0 | 1, TeamPalette>>) {
    if (teamPalettes) this.teamPalettes = teamPalettes;
    return this.lastIdentityKey !== this.identityKey();
  }

  private refreshHoverScale() {
    if (!this.isLive()) return;
    const boost = this.hovered || this.selected ? 1.1 : 1;
    this.sprite.scale.set(this.baseScale * boost);
  }

  private redrawOutline(cellSize: number) {
    this.outline.clear();
    if (!this.selected) return;
    const r = cellSize * 0.4;
    this.outline.setStrokeStyle({
      width: Math.max(2, cellSize * 0.045),
      color: 0x38bdf8,
      alpha: 0.95,
    });
    this.outline.circle(0, 0, r);
    this.outline.stroke();
  }

  private disposeValueBadge() {
    if (!this.valueBadge) return;
    const badge = this.valueBadge;
    this.valueBadge = null;
    if (badge.parent) badge.parent.removeChild(badge);
    try {
      badge.destroy({ children: true, texture: false, textureSource: false });
    } catch {
      // Pixi text pool can already be torn down during app navigate/unmount.
    }
  }

  override destroy(options?: boolean | Parameters<Container['destroy']>[0]) {
    this.visualGen += 1;
    this.disposeValueBadge();
    super.destroy(options);
  }
}
